import { useCallback, type JSX } from 'react';

import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useTranslation } from 'react-i18next';

import type { JizdaListItem, JizdaResponse } from '@/api/types/Jizda';

import { OrderEditor } from './OrderEditor';

// Right-side detail panel (ADR-0033 / DESIGN.md §8.2 master-detail adapted to a
// right column). Provides the Vapas card chrome (§6.3), the "Detail" header
// (green inverse chip + customer + #id + close-X), and the §8.9 empty-state
// placeholder shown until a row is selected. The editable form itself is
// `OrderEditor variant="panel"` — one editor, two render targets.

export interface OrderDetailPanelProps {
  // The selected jízda's list item (header source: customer + id). `null` →
  // empty-state placeholder.
  order: JizdaListItem | null;
  onClose: () => void;
  onSaved: (order: JizdaResponse) => void;
  onDeleted: (orderId: string) => void;
  // Opens the audit-history modal (CHR-04: the history affordance lives in the
  // detail header, left of the #hash — moved out of the overview table).
  onOpenHistory: (orderId: string) => void;
}

const EMPTY_ICON_SIZE_PX = 72;

export const OrderDetailPanel = ({
  order,
  onClose,
  onSaved,
  onDeleted,
  onOpenHistory,
}: OrderDetailPanelProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');

  // OrderEditor.handleSave (intentionally NOT modified — ADR-0032 protected
  // logic) calls `onClose(true)` after a successful save. In the modal that
  // closes it; in the PANEL we want it to STAY open + refreshed, so we swallow
  // exactly that post-save auto-close. Every other close path — the X button,
  // the footer Cancel, AND the dispatcher-reassign path (which calls `onSaved`
  // but NOT `onClose`) — passes `didSave` falsy and closes normally. Threading
  // the explicit `didSave` signal (vs the old latched `justSavedRef`) avoids the
  // stale-flag bug where a reassign left the latch stuck and ate the next
  // user Cancel.
  const handleEditorClose = useCallback(
    (didSave?: boolean) => {
      if (didSave === true) {
        // Swallow the post-save auto-close — keep the panel open + refreshed.
        return;
      }
      onClose();
    },
    [onClose],
  );

  return (
    // Vapas `.card` (DESIGN.md §6.3): paper bg, divider border, 12px radius,
    // shadow-1. Independently scrollable flex column (CHR-01): the card clamps
    // to the master-detail row height (overflow:hidden) and its inner editor
    // body owns the vertical scroll, decoupled from the left orders list.
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3, // 12px (DESIGN.md §4 card radius)
        boxShadow: theme.elevation.shadow1,
        overflow: 'hidden',
      }}
      data-testid="order-detail-panel"
    >
      {order === null ? (
        // §8.9 empty-state placeholder — large icon in a beige circle, H3,
        // two-line description. No CTA (selection is the action).
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, p: theme.spacing(4), textAlign: 'center' }}
          data-testid="order-detail-empty"
        >
          <Box
            sx={{
              width: EMPTY_ICON_SIZE_PX,
              height: EMPTY_ICON_SIZE_PX,
              borderRadius: '999px',
              bgcolor: theme.palette.background.default,
              display: 'grid',
              placeItems: 'center',
              color: theme.palette.text.disabled,
            }}
          >
            <InboxOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: 16, color: theme.palette.text.primary }}
          >
            {t('panel.emptyTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, maxWidth: 280 }}
          >
            {t('panel.emptyBody')}
          </Typography>
        </Stack>
      ) : (
        <>
          {/* Panel header — green inverse "Detail" chip + customer + #id + X */}
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              p: theme.spacing(1.5, 2),
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                flexShrink: 0,
              }}
              data-testid="order-detail-chip"
            >
              {t('panel.detailChip')}
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
              data-testid="order-detail-customer"
            >
              {order.customer?.name ?? t('editor.titleEditNoCustomer')}
            </Typography>
            {/* Smazáno badge (CHR-04 / ADR-0043) — a soft-deleted jízda is
                viewable but read-only; the badge makes the state unmistakable
                in the detail header. */}
            {order.deleted ? (
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  bgcolor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  flexShrink: 0,
                }}
                data-testid="order-detail-deleted-badge"
              >
                {t('panel.deletedBadge')}
              </Box>
            ) : null}
            {/* History affordance (CHR-04) — moved here from the overview
                table; sits immediately left of the #hash caption. */}
            <Tooltip title={t('column.history')} placement="top">
              <IconButton
                size="small"
                onClick={() => onOpenHistory(order.id)}
                sx={{ color: theme.palette.secondary.main, flexShrink: 0 }}
                aria-label={t('column.history')}
                data-testid="order-detail-history"
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                fontFamily: theme.monospaceFontFamily,
                color: theme.palette.text.secondary,
                flexShrink: 0,
              }}
            >
              #{order.id.slice(-8)}
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label={t('editor.close')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          {/* Editable form — SAME body + save logic as the modal (ADR-0033).
              `key` forces a clean remount when the selected order changes so
              the form re-fetches + resets state. */}
          <OrderEditor
            key={order.id}
            orderId={order.id}
            variant="panel"
            onClose={handleEditorClose}
            onSaved={onSaved}
            onDeleted={onDeleted}
          />
        </>
      )}
    </Box>
  );
};
